import React, { useState } from "react";
import {
  Home,
  Heart,
  Plane,
  Shield,
  Building2,
  Scale,
  ScrollText,
  UserX,
  Briefcase,
  Lightbulb,
  Receipt,
  Leaf,
  AlertTriangle,
  Landmark,
  HardHat,
  ShieldCheck,
  Film,
  Cpu,
  MoreHorizontal,
  Check,
} from "lucide-react";
import PracticeAreaRequestModal from "../../ui/PracticeAreaRequestModal";

// ── Practice Area Definitions ─────────────────────────────────────────

export interface PracticeAreaOption {
  value: string;
  label: string;
  description: string;
  icon: React.FC<any>;
  color: string; // Tailwind bg class
  iconColor: string; // Tailwind text class
}

export const PRACTICE_AREA_OPTIONS: PracticeAreaOption[] = [
  {
    value: "real_estate",
    label: "Real Estate",
    description: "Property transactions, closings & title work",
    icon: Home,
    color: "bg-cyan-50",
    iconColor: "text-cyan-600",
  },
  {
    value: "family",
    label: "Family Law",
    description: "Divorce, custody, support & adoption",
    icon: Heart,
    color: "bg-pink-50",
    iconColor: "text-pink-600",
  },
  {
    value: "immigration",
    label: "Immigration",
    description: "Visas, PR applications & citizenship",
    icon: Plane,
    color: "bg-emerald-50",
    iconColor: "text-emerald-600",
  },
  {
    value: "criminal",
    label: "Criminal Defence",
    description: "Criminal charges, trials & appeals",
    icon: Shield,
    color: "bg-red-50",
    iconColor: "text-red-600",
  },
  {
    value: "corporate",
    label: "Corporate & Commercial",
    description: "Business formation, M&A & contracts",
    icon: Building2,
    color: "bg-purple-50",
    iconColor: "text-purple-600",
  },
  {
    value: "litigation",
    label: "Civil Litigation",
    description: "Disputes, trials & enforcement",
    icon: Scale,
    color: "bg-blue-50",
    iconColor: "text-blue-600",
  },
  {
    value: "wills_estates",
    label: "Wills, Estates & Trusts",
    description: "Estate planning, probate & trusts",
    icon: ScrollText,
    color: "bg-indigo-50",
    iconColor: "text-indigo-600",
  },
  {
    value: "personal_injury",
    label: "Personal Injury",
    description: "Accident claims & medical negligence",
    icon: UserX,
    color: "bg-orange-50",
    iconColor: "text-orange-600",
  },
  {
    value: "employment_labour",
    label: "Employment & Labour",
    description: "Workplace disputes, contracts & compliance",
    icon: Briefcase,
    color: "bg-amber-50",
    iconColor: "text-amber-600",
  },
  {
    value: "intellectual_property",
    label: "Intellectual Property",
    description: "Patents, trademarks, copyright & trade secrets",
    icon: Lightbulb,
    color: "bg-yellow-50",
    iconColor: "text-yellow-600",
  },
  {
    value: "tax",
    label: "Tax Law",
    description: "Tax planning, disputes & compliance",
    icon: Receipt,
    color: "bg-lime-50",
    iconColor: "text-lime-600",
  },
  {
    value: "environmental",
    label: "Environmental Law",
    description: "Environmental compliance & regulation",
    icon: Leaf,
    color: "bg-green-50",
    iconColor: "text-green-600",
  },
  {
    value: "bankruptcy_insolvency",
    label: "Bankruptcy & Insolvency",
    description: "Debt restructuring & creditor rights",
    icon: AlertTriangle,
    color: "bg-rose-50",
    iconColor: "text-rose-600",
  },
  {
    value: "administrative",
    label: "Administrative Law",
    description: "Government regulation & tribunals",
    icon: Landmark,
    color: "bg-slate-50",
    iconColor: "text-slate-600",
  },
  {
    value: "construction",
    label: "Construction Law",
    description: "Construction disputes, liens & contracts",
    icon: HardHat,
    color: "bg-stone-50",
    iconColor: "text-stone-600",
  },
  {
    value: "insurance",
    label: "Insurance Law",
    description: "Coverage disputes & subrogation",
    icon: ShieldCheck,
    color: "bg-sky-50",
    iconColor: "text-sky-600",
  },
  {
    value: "entertainment",
    label: "Entertainment & Media",
    description: "Entertainment contracts & content licensing",
    icon: Film,
    color: "bg-fuchsia-50",
    iconColor: "text-fuchsia-600",
  },
  {
    value: "technology",
    label: "Technology & Cyber",
    description: "Data privacy, SaaS & tech transactions",
    icon: Cpu,
    color: "bg-violet-50",
    iconColor: "text-violet-600",
  },
  {
    value: "other",
    label: "Other",
    description: "Other area of practice",
    icon: MoreHorizontal,
    color: "bg-gray-50",
    iconColor: "text-gray-500",
  },
];

// ── Component ──────────────────────────────────────────────────────────

interface PracticeAreaStepProps {
  selected: string[];
  onChange: (areas: string[]) => void;
}

const PracticeAreaStep: React.FC<PracticeAreaStepProps> = ({
  selected,
  onChange,
}) => {
  const [requestModalOpen, setRequestModalOpen] = useState(false);

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-900">
          What does your firm practice?
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Select all areas of law your firm handles. This personalizes your
          sidebar and available workflows.
        </p>
      </div>

      {/* Selection count */}
      {selected.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-brand-light text-brand">
            <Check className="h-3 w-3" />
            {selected.length} selected
          </span>
        </div>
      )}

      {/* Practice area cards grid */}
      <div className="grid grid-cols-2 gap-3 max-h-[420px] overflow-y-auto pr-1">
        {PRACTICE_AREA_OPTIONS.map((area) => {
          const Icon = area.icon;
          const isSelected = selected.includes(area.value);
          return (
            <button
              key={area.value}
              type="button"
              onClick={() => toggle(area.value)}
              className={`relative flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                isSelected
                  ? "border-brand-accent bg-brand-light/50 shadow-sm"
                  : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
              }`}
            >
              {/* Check indicator */}
              {isSelected && (
                <div className="absolute top-2.5 right-2.5 h-5 w-5 rounded-full bg-brand-accent flex items-center justify-center">
                  <Check className="h-3 w-3 text-white" />
                </div>
              )}

              <div
                className={`h-9 w-9 rounded-lg ${area.color} flex items-center justify-center flex-shrink-0`}
              >
                <Icon className={`h-4.5 w-4.5 ${area.iconColor}`} />
              </div>
              <div className="min-w-0 pr-5">
                <p className="text-sm font-semibold text-slate-800 leading-tight">
                  {area.label}
                </p>
                <p className="text-xs text-slate-500 mt-0.5 leading-snug">
                  {area.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Custom practice area note */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-700">
            Don't see your practice area?
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            We'll build a custom workflow for your firm, free of charge.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setRequestModalOpen(true)}
          className="flex-shrink-0 px-4 py-2 text-xs font-semibold rounded-lg border-2 transition-all hover:shadow-sm"
          style={{ borderColor: "#0F5132", color: "#0F5132" }}
        >
          Request one
        </button>
      </div>
      <PracticeAreaRequestModal isOpen={requestModalOpen} onClose={() => setRequestModalOpen(false)} />

      <p className="text-xs text-slate-400">
        You can change these anytime in Settings &rarr; Firm Profile
      </p>
    </div>
  );
};

export default PracticeAreaStep;
